import { useMemo, useRef, useEffect, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import gsap from 'gsap';
import { useGameStore } from '../../core/store';
import { useIsMobile } from '../../core/useIsMobile';
import { FootballShirt } from './FootballShirt';

const STALL_W = 0.95;
const STALL_COUNT = 5;
const ROW_Z = -4.75;
const ROW_Y = 0;
const STALL_H = 2.55;
const STALL_D = 0.72;

const woodDark = '#221814';
const woodMid = '#32241c';
const woodLit = '#3a2218';
const benchRed = '#6b1c1c';

/**
 * Cinematic open, shared by every light in the room (desktop AND mobile —
 * both read the same Canvas clock, so this is identical on both): a true
 * held blackout, then every fixture ramps up together as the camera begins
 * its slow push (see LockerCamera's GSAP timeline). Multiply any light/
 * emissive target by this before assigning.
 */
const BLACKOUT_HOLD_S = 0.7;
const LIGHT_FADE_S = 2.3;
function revealProgress(elapsedSeconds: number): number {
  return THREE.MathUtils.smoothstep(
    elapsedSeconds,
    BLACKOUT_HOLD_S,
    BLACKOUT_HOLD_S + LIGHT_FADE_S,
  );
}

function ConcreteFloor() {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow position={[0, 0, 0]}>
        <planeGeometry args={[22, 26]} />
        <meshStandardMaterial
          color="#101014"
          roughness={0.72}
          metalness={0.22}
          envMapIntensity={0.35}
        />
      </mesh>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
        position={[0, 0.002, ROW_Z + 1.2]}
      >
        <planeGeometry args={[STALL_W * STALL_COUNT + 0.6, 3.2]} />
        <meshStandardMaterial
          color="#16161c"
          roughness={0.55}
          metalness={0.35}
        />
      </mesh>
    </group>
  );
}

function RoomShell() {
  const wall = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#121216',
        roughness: 0.92,
        metalness: 0.04,
      }),
    [],
  );
  const tube = useRef<THREE.MeshStandardMaterial>(null);
  useFrame(({ clock }) => {
    if (tube.current) {
      tube.current.emissiveIntensity = 0.35 * revealProgress(clock.elapsedTime);
    }
  });
  const span = STALL_W * STALL_COUNT + 2.4;
  return (
    <group>
      <mesh position={[0, 2.1, -5.55]} material={wall} receiveShadow>
        <boxGeometry args={[span + 4, 4.4, 0.4]} />
      </mesh>
      <mesh position={[-span * 0.5 - 1.2, 2.1, 0]} material={wall} receiveShadow>
        <boxGeometry args={[0.35, 4.4, 14]} />
      </mesh>
      <mesh position={[span * 0.5 + 1.2, 2.1, 0]} material={wall} receiveShadow>
        <boxGeometry args={[0.35, 4.4, 14]} />
      </mesh>
      <mesh position={[0, 4.25, 0]} material={wall}>
        <boxGeometry args={[span + 4, 0.25, 14]} />
      </mesh>
      {[-1.8, 0, 1.8].map((x) => (
        <mesh key={x} position={[x, 4.05, -2.5]}>
          <boxGeometry args={[0.14, 0.2, 10]} />
          <meshStandardMaterial color="#0a0a0e" roughness={0.85} metalness={0.2} />
        </mesh>
      ))}
      <mesh position={[0, 3.85, ROW_Z + 0.3]}>
        <boxGeometry args={[STALL_W * 1.15, 0.04, 0.08]} />
        <meshStandardMaterial
          ref={tube}
          color="#e8eef8"
          emissive="#c8d4e8"
          emissiveIntensity={0}
          toneMapped={false}
          roughness={0.4}
        />
      </mesh>
    </group>
  );
}

/** Per-stall props on dark side bays only (no kits). */
function StallProps({ variant }: { variant: number }) {
  const v = ((variant % 5) + 5) % 5;
  return (
    <group>
      {v === 1 && (
        <>
          {/* Boot bag */}
          <mesh position={[0.18, 0.38, 0.08]} rotation={[0.05, 0.4, 0]} castShadow>
            <boxGeometry args={[0.2, 0.16, 0.28]} />
            <meshStandardMaterial color="#0c0c10" roughness={0.88} metalness={0.06} />
          </mesh>
          <mesh position={[-0.2, 0.3, 0.05]} castShadow>
            <boxGeometry args={[0.14, 0.025, 0.1]} />
            <meshStandardMaterial color="#1a2030" roughness={0.85} />
          </mesh>
        </>
      )}
      {v === 2 && (
        <>
          <mesh position={[0.22, 0.36, 0.1]} castShadow>
            <cylinderGeometry args={[0.03, 0.034, 0.18, 10]} />
            <meshStandardMaterial color="#0d1520" roughness={0.45} metalness={0.2} />
          </mesh>
          <mesh
            position={[-0.18, 0.3, 0.06]}
            rotation={[Math.PI / 2, 0, 0.3]}
            castShadow
          >
            <cylinderGeometry args={[0.032, 0.032, 0.045, 12]} />
            <meshStandardMaterial color="#e8e8ea" roughness={0.75} />
          </mesh>
        </>
      )}
      {v === 3 && (
        <>
          {/* Shin pads */}
          <mesh position={[-0.16, 0.32, 0.05]} rotation={[0.2, 0.1, 0]} castShadow>
            <boxGeometry args={[0.08, 0.04, 0.2]} />
            <meshStandardMaterial color="#1a1a22" roughness={0.55} metalness={0.15} />
          </mesh>
          <mesh position={[-0.05, 0.32, 0.05]} rotation={[0.2, -0.1, 0]} castShadow>
            <boxGeometry args={[0.08, 0.04, 0.2]} />
            <meshStandardMaterial color="#1a1a22" roughness={0.55} metalness={0.15} />
          </mesh>
          <mesh position={[0.22, 0.3, 0.04]} castShadow>
            <boxGeometry args={[0.12, 0.03, 0.09]} />
            <meshStandardMaterial color="#2a1810" roughness={0.8} />
          </mesh>
        </>
      )}
      {v === 4 && (
        <>
          <mesh position={[0.2, 0.35, 0.08]} castShadow>
            <cylinderGeometry args={[0.028, 0.032, 0.2, 10]} />
            <meshStandardMaterial color="#102030" roughness={0.4} metalness={0.25} />
          </mesh>
          <mesh position={[-0.2, 0.385, 0.05]} rotation={[0, 0.25, 0]} castShadow>
            <boxGeometry args={[0.18, 0.12, 0.14]} />
            <meshStandardMaterial color="#0a0a0e" roughness={0.9} />
          </mesh>
        </>
      )}
    </group>
  );
}

function LockerStall({
  x,
  lit = false,
  index = 0,
}: {
  x: number;
  lit?: boolean;
  index: number;
}) {
  const doorClosed = useGameStore((s) => s.lockerDoorClosed);
  const cine = useGameStore((s) => s.lockerCine);
  const mobile = useIsMobile();
  const led = useRef<THREE.MeshStandardMaterial>(null);
  const fill = useRef<THREE.PointLight>(null);
  const ankleFill = useRef<THREE.PointLight>(null);
  const stallSpot = useRef<THREE.SpotLight>(null);

  useFrame(({ clock }) => {
    if (!lit) return;
    const on = doorClosed ? 0 : 1;
    const reveal = revealProgress(clock.elapsedTime);
    const bootFlicker =
      reveal >= 1 &&
      !doorClosed &&
      !cine.on &&
      clock.elapsedTime > BLACKOUT_HOLD_S + LIGHT_FADE_S + 0.4 &&
      clock.elapsedTime < BLACKOUT_HOLD_S + LIGHT_FADE_S + 0.9
        ? 0.55 + Math.random() * 0.45
        : 1;
    const flicker = cine.on ? cine.flicker : bootFlicker;
    const wash = cine.on ? cine.wash : 1;
    const spotBoost = cine.on ? cine.spot : 1;
    // Phones crush midtones — lift the hero stall fill a bit
    const mobileLift = mobile ? 1.22 : 1;

    if (led.current) {
      led.current.emissiveIntensity = THREE.MathUtils.damp(
        led.current.emissiveIntensity,
        2.4 * on * flicker * reveal * spotBoost * (mobile ? 1.1 : 1),
        4,
        0.016,
      );
    }
    if (fill.current) {
      fill.current.intensity = THREE.MathUtils.damp(
        fill.current.intensity,
        (cine.on ? 6 : 24) * on * flicker * reveal * wash * spotBoost * mobileLift,
        4,
        0.016,
      );
    }
    if (ankleFill.current) {
      ankleFill.current.intensity = THREE.MathUtils.damp(
        ankleFill.current.intensity,
        (cine.on ? 0 : 7) * on * flicker * reveal * wash * mobileLift,
        4,
        0.016,
      );
    }
    if (stallSpot.current) {
      stallSpot.current.intensity = THREE.MathUtils.damp(
        stallSpot.current.intensity,
        (cine.on ? 18 : 0) * on * flicker * reveal * spotBoost,
        5,
        0.016,
      );
    }
  });

  const depth = STALL_D;
  const h = STALL_H;
  const panel = lit ? woodLit : woodDark;
  const back = lit ? '#4a2018' : woodMid;
  const rodY = h - 0.38;
  const bayNum = String(10 + index).padStart(2, '0');

  return (
    <group position={[x, ROW_Y, ROW_Z]}>
      <mesh position={[0, h * 0.5, -depth * 0.5]} castShadow receiveShadow>
        <boxGeometry args={[STALL_W - 0.04, h, 0.06]} />
        <meshStandardMaterial
          color={back}
          roughness={0.88}
          metalness={0.05}
          emissive={lit ? '#5a1810' : '#000000'}
          emissiveIntensity={lit ? 0.3 : 0}
        />
      </mesh>

      <mesh position={[0, h - 0.06, -0.12]} castShadow receiveShadow>
        <boxGeometry args={[STALL_W - 0.02, 0.08, depth - 0.08]} />
        <meshStandardMaterial color={panel} roughness={0.78} metalness={0.08} />
      </mesh>

      <mesh position={[-STALL_W * 0.5, h * 0.5, -0.08]} castShadow receiveShadow>
        <boxGeometry args={[0.045, h, depth]} />
        <meshStandardMaterial color={panel} roughness={0.8} metalness={0.06} />
      </mesh>
      <mesh position={[STALL_W * 0.5, h * 0.5, -0.08]} castShadow receiveShadow>
        <boxGeometry args={[0.045, h, depth]} />
        <meshStandardMaterial color={panel} roughness={0.8} metalness={0.06} />
      </mesh>

      <mesh
        position={[0, rodY, 0.02]}
        rotation={[0, 0, Math.PI / 2]}
        castShadow
        receiveShadow
      >
        <cylinderGeometry args={[0.018, 0.018, STALL_W - 0.1, 20]} />
        <meshStandardMaterial color="#141418" metalness={0.9} roughness={0.35} />
      </mesh>

      <mesh position={[0, h - 0.22, 0.26]}>
        <boxGeometry args={[0.16, 0.06, 0.012]} />
        <meshStandardMaterial color="#0a0a0c" roughness={0.55} metalness={0.35} />
      </mesh>
      <Html
        position={[0, h - 0.22, 0.28]}
        center
        distanceFactor={4.5}
        style={{
          pointerEvents: 'none',
          color: lit ? '#c9a227' : '#666870',
          fontFamily: 'Bebas Neue, Impact, sans-serif',
          fontSize: '18px',
          letterSpacing: '2px',
          opacity: lit ? 0.95 : 0.45,
        }}
      >
        {bayNum}
      </Html>

      {[-0.14, 0, 0.14].map((vx) => (
        <mesh key={vx} position={[vx, 0.55, -depth * 0.5 + 0.03]}>
          <cylinderGeometry args={[0.012, 0.012, 0.02, 6]} />
          <meshStandardMaterial color="#050506" roughness={1} />
        </mesh>
      ))}

      <mesh position={[0, 0.22, 0.02]} castShadow receiveShadow>
        <boxGeometry args={[STALL_W - 0.12, 0.1, 0.42]} />
        <meshStandardMaterial
          color={benchRed}
          roughness={0.72}
          metalness={0.05}
          emissive={lit ? '#401010' : '#000000'}
          emissiveIntensity={lit ? 0.22 : 0}
        />
      </mesh>
      <mesh position={[0, 0.12, 0.02]}>
        <boxGeometry args={[STALL_W - 0.18, 0.12, 0.36]} />
        <meshStandardMaterial color="#141010" roughness={0.85} />
      </mesh>

      {!lit && <StallProps variant={index} />}

      {lit && (
        <>
          <mesh position={[0, h - 0.14, -0.28]}>
            <boxGeometry args={[STALL_W - 0.22, 0.02, 0.04]} />
            <meshStandardMaterial
              ref={led}
              color="#f5f8ff"
              emissive="#eef4ff"
              emissiveIntensity={2.4}
              toneMapped={false}
            />
          </mesh>
          <pointLight
            ref={fill}
            position={[0, h - 0.35, 0.05]}
            color="#ffb090"
            intensity={24}
            distance={4.5}
            decay={1.8}
            castShadow
            shadow-mapSize={[512, 512]}
            shadow-bias={-0.0002}
            shadow-normalBias={0.035}
            shadow-radius={6}
            shadow-intensity={0.42}
          />
          <pointLight
            ref={ankleFill}
            position={[0, 0.55, 0.45]}
            color="#ffc8a0"
            intensity={0}
            distance={3.5}
            decay={2}
          />
          {/* Claim-only hard spot — off during normal room */}
          <spotLight
            ref={stallSpot}
            position={[0, h - 0.15, 0.7]}
            angle={0.4}
            penumbra={0.4}
            intensity={0}
            color="#fff2e8"
            distance={3.8}
            decay={1.8}
            castShadow={false}
          />
          <mesh position={[-0.26, 0.3, 0.05]} rotation={[0, 0.2, 0]} castShadow>
            <boxGeometry args={[0.16, 0.03, 0.11]} />
            <meshStandardMaterial color="#1a1c22" roughness={0.85} />
          </mesh>
          <mesh position={[0.24, 0.34, 0.1]} castShadow>
            <cylinderGeometry args={[0.028, 0.032, 0.17, 10]} />
            <meshStandardMaterial color="#0d1520" roughness={0.4} metalness={0.2} />
          </mesh>
        </>
      )}
    </group>
  );
}

function HungJersey() {
  const name = useGameStore((s) => s.profile.name);
  const number = useGameStore((s) => s.profile.number);
  const foot = useGameStore((s) => s.profile.foot);
  const beat = useGameStore((s) => s.lockerBeat);
  const touched = useGameStore((s) => s.lockerClaimTouched);
  const dissolve = useGameStore((s) => s.lockerDissolve);
  // Keep cloth mounted through dissolve; after fade, empty hanger+hook stays visible
  const shirtGone =
    (touched && dissolve >= 0.995) ||
    beat === 'closing' ||
    beat === 'walk';
  // Always mount FootballShirt so the hanger hook remains after cloth is gone
  const showCloth = !shirtGone;
  const group = useRef<THREE.Group>(null);
  const pull = useRef({ z: 0, rx: 0, ry: 0 });
  const basePos = useRef({ y: 0, z: 0 });

  const rodY = STALL_H - 0.38;
  const rodZ = 0.02;

  useEffect(() => {
    if (!group.current) return;
    basePos.current.y = rodY - 0.012;
    basePos.current.z = rodZ;
  }, [rodY, rodZ]);

  useEffect(() => {
    // After dissolve: settle empty hanger slightly forward — no yank
    if (!shirtGone) {
      gsap.to(pull.current, { z: 0, rx: 0, ry: 0, duration: 0.35, ease: 'power2.out' });
      return;
    }
    gsap.to(pull.current, {
      z: 0.06,
      rx: 0.04,
      ry: 0.18,
      duration: 0.55,
      ease: 'power2.out',
    });
  }, [shirtGone]);

  useFrame(({ clock }) => {
    if (!group.current) return;
    const t = clock.elapsedTime;
    // Almost still while dissolving; soft sway on empty hanger
    const dissolving = beat === 'claim' && dissolve > 0 && dissolve < 1;
    const sway = shirtGone ? 0.45 : dissolving ? 0.05 : 1;

    const baseZ = shirtGone ? 0.06 : 0;
    const baseRy = shirtGone ? 0.32 : 0.22;
    const baseRx = shirtGone ? 0.04 : 0;

    group.current.position.y = basePos.current.y;
    group.current.position.z =
      basePos.current.z + baseZ + pull.current.z;
    group.current.position.x = shirtGone ? 0.06 : 0;
    group.current.rotation.y =
      baseRy +
      (pull.current.ry ?? 0) +
      Math.sin(t * 0.4) * 0.03 * sway;
    group.current.rotation.z = Math.sin(t * 0.5) * 0.012 * sway;
    group.current.rotation.x =
      baseRx + pull.current.rx + Math.sin(t * 0.32) * 0.008 * sway;
  });

  return (
    <group position={[0, ROW_Y, ROW_Z]}>
      <group ref={group} position={[0, rodY - 0.012, rodZ]} rotation={[0, 0.22, 0]}>
        <Suspense
          fallback={
            <mesh position={[0, -0.45, 0]}>
              <boxGeometry args={[0.35, 0.5, 0.06]} />
              <meshStandardMaterial color="#006600" />
            </mesh>
          }
        >
          <FootballShirt
            name={name}
            number={number}
            height={1.2}
            showShirt={showCloth}
            dissolve={shirtGone ? 1 : dissolve}
          />
        </Suspense>
      </group>

      <group position={[0, 0.32, 0.08]}>
        <mesh castShadow position={[-0.14, 0, 0]} rotation={[0.15, 0.12, 0]}>
          <boxGeometry args={[0.11, 0.08, 0.26]} />
          <meshStandardMaterial
            color="#0e0e12"
            roughness={0.4}
            metalness={0.45}
            emissive={foot === 'left' ? '#c9a227' : '#000000'}
            emissiveIntensity={foot === 'left' ? 0.7 : 0}
          />
        </mesh>
        <mesh castShadow position={[0.14, 0, 0]} rotation={[0.15, -0.12, 0]}>
          <boxGeometry args={[0.11, 0.08, 0.26]} />
          <meshStandardMaterial
            color="#0e0e12"
            roughness={0.4}
            metalness={0.45}
            emissive={foot === 'right' ? '#c9a227' : '#000000'}
            emissiveIntensity={foot === 'right' ? 0.7 : 0}
          />
        </mesh>
      </group>
    </group>
  );
}

function ExitDoor({ open }: { open: boolean }) {
  const light = useRef<THREE.PointLight>(null);
  useFrame(() => {
    if (!light.current) return;
    light.current.intensity = THREE.MathUtils.damp(
      light.current.intensity,
      open ? 90 : 0,
      2.5,
      0.016,
    );
  });
  return (
    <group position={[0, 1.6, 6.2]}>
      <mesh>
        <boxGeometry args={[2.2, 3.2, 0.2]} />
        <meshStandardMaterial color="#121218" roughness={0.6} metalness={0.3} />
      </mesh>
      <mesh position={[0, 0, -0.15]}>
        <planeGeometry args={[1.6, 2.6]} />
        <meshBasicMaterial
          color="#fff4d8"
          transparent
          opacity={open ? 0.85 : 0}
          toneMapped={false}
        />
      </mesh>
      <pointLight
        ref={light}
        position={[0, 0.2, -1.5]}
        color="#fff0c8"
        intensity={0}
        distance={18}
        decay={2}
      />
    </group>
  );
}

/** Floor kit crate + spare boots in the aisle — cinematic clutter, still sparse */
function AisleDressing() {
  return (
    <group position={[0, 0, ROW_Z + 1.6]}>
      <mesh position={[-1.6, 0.12, 0.2]} castShadow receiveShadow>
        <boxGeometry args={[0.45, 0.24, 0.35]} />
        <meshStandardMaterial color="#1a1410" roughness={0.85} metalness={0.05} />
      </mesh>
      <mesh position={[1.7, 0.08, -0.1]} rotation={[0.1, 0.5, 0]} castShadow>
        <boxGeometry args={[0.12, 0.08, 0.28]} />
        <meshStandardMaterial color="#0e0e12" roughness={0.4} metalness={0.4} />
      </mesh>
      <mesh position={[1.9, 0.08, -0.05]} rotation={[0.1, -0.3, 0]} castShadow>
        <boxGeometry args={[0.12, 0.08, 0.28]} />
        <meshStandardMaterial color="#0e0e12" roughness={0.4} metalness={0.4} />
      </mesh>
    </group>
  );
}

/** Base room wash — starts fully black, ramps in with every other fixture. */
function AmbientReveal() {
  const ambient = useRef<THREE.AmbientLight>(null);
  const hemi = useRef<THREE.HemisphereLight>(null);
  const dir = useRef<THREE.DirectionalLight>(null);
  const key = useRef<THREE.SpotLight>(null);
  const keyTarget = useRef<THREE.Object3D>(null);
  const cine = useGameStore((s) => s.lockerCine);
  const mobile = useIsMobile();

  useEffect(() => {
    if (key.current && keyTarget.current) {
      key.current.target = keyTarget.current;
      key.current.target.updateMatrixWorld();
    }
  }, []);

  useFrame(({ clock }) => {
    const reveal = revealProgress(clock.elapsedTime);
    const wash = cine.on ? cine.wash : 1;
    const spot = cine.on ? cine.spot * cine.flicker : 1;
    const lift = mobile ? 1.35 : 1;
    if (ambient.current) ambient.current.intensity = 0.06 * reveal * wash * lift;
    if (hemi.current) hemi.current.intensity = 0.16 * reveal * wash * lift;
    if (dir.current) dir.current.intensity = 0.08 * reveal * wash * (mobile ? 1.5 : 1);
    // Key spot only really used for the claim print beat
    if (key.current) {
      key.current.intensity = (cine.on ? 14 : 0) * reveal * spot;
    }
  });

  return (
    <>
      <ambientLight ref={ambient} intensity={0} color={mobile ? '#2a2430' : '#221c28'} />
      <hemisphereLight
        ref={hemi}
        args={[mobile ? '#3e3448' : '#32283c', '#0a0806', 0]}
      />
      <directionalLight
        ref={dir}
        position={[1.5, 5, 3]}
        intensity={0}
        color={mobile ? '#c4ccd8' : '#a8b0c8'}
        castShadow={false}
      />
      <object3D ref={keyTarget} position={[0, 1.2, ROW_Z]} />
      <spotLight
        ref={key}
        position={[0, 3.6, ROW_Z + 1.8]}
        angle={0.42}
        penumbra={0.55}
        intensity={0}
        color="#ffe8d4"
        distance={10}
        decay={1.6}
        castShadow={false}
      />
    </>
  );
}

export function LockerRoomWorld() {
  const particles = useGameStore((s) => s.lockerCine.particles);

  const stalls = useMemo(() => {
    const half = Math.floor(STALL_COUNT / 2);
    return Array.from({ length: STALL_COUNT }, (_, i) => {
      const idx = i - half;
      return (
        <LockerStall
          key={idx}
          x={idx * STALL_W}
          lit={idx === 0}
          index={idx}
        />
      );
    });
  }, []);

  return (
    <group>
      <color attach="background" args={['#050508']} />
      <fog attach="fog" args={['#07070a', 9, 24]} />
      <AmbientReveal />

      <ConcreteFloor />
      <RoomShell />
      {stalls}
      <HungJersey />
      <AisleDressing />
      <ExitDoor open={false} />

      <Sparkles
        count={particles ? 36 : 12}
        scale={particles ? [2.2, 2.4, 1.0] : [2.8, 2.2, 1.2]}
        size={particles ? 1.2 : 0.85}
        speed={particles ? 0.18 : 0.06}
        color="#ffd0a8"
        opacity={particles ? 0.45 : 0.2}
        position={[0, 1.55, ROW_Z + 0.35]}
      />
    </group>
  );
}
